import {
  COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY,
  FRIENDLY_NAMES_BY_DOMAIN_TYPE,
  HOVER_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY,
  SELECTED_STROKE_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY,
} from 'antibodies/AntibodyConstants';
import {AntibodyFormat, AntibodyFormatDomain, DistinctDomain} from 'antibodies/AntibodyTypedefs';
import {assert} from 'Asserts';
import {AntibodyDomainType} from 'graphql-client/__generated/graphqlGlobalTypes';

export function distinctDomainToColor(
  {type, specificity}: DistinctDomain,
  colorBank: Record<AntibodyDomainType, Array<string>> = COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY
): string {
  const index = specificity ? 'abcdefgh'.indexOf(specificity) : 0;
  assert(index !== -1, `Invalid specificity: received ${specificity}, expected a-h`);
  const colors = colorBank[type];
  assert(index < colors.length, `Not enough colors configured for ${type} specificity ${specificity}`);
  return colors[index];
}

/**
 * Get a short display name for the associated entity field for this distinct domain.
 * Intended use is for inline labels, such as those in schema configuration or antibody viz.
 *
 * Keep in sync with get_antibody_domain_field_name in benchling/antibodies/format/domain/util.py
 */
export function distinctDomainToFieldName({type, specificity}: DistinctDomain): string {
  return `${type}${specificity ?? ''}`;
}

/**
 * Get a long display name for the associated entity field for this distinct domain.
 * Intended use is for displaying above an input where one can view and/or set an entity for this field.
 */
export function distinctDomainToFieldLabel({type, specificity}: DistinctDomain): string {
  let friendlyName = FRIENDLY_NAMES_BY_DOMAIN_TYPE[type];
  let abbreviation = `${type}`;
  if (specificity != null) {
    friendlyName = `${friendlyName} ${specificity}`;
    abbreviation = `${abbreviation}${specificity}`;
  }
  return `${friendlyName} (${abbreviation})`;
}

/**
 * Get a stringy key for this distinct domain.
 * Intended use is for managing the React render cycle; i.e. passing as a `key` prop.
 */
export function distinctDomainToKey({type, specificity}: DistinctDomain): string {
  return specificity != null ? `${type}-${specificity}` : `${type}`;
}

export function domainColor({
  distinctDomain,
  hovered,
  selected,
}: {
  distinctDomain: DistinctDomain;
  hovered?: boolean;
  selected?: boolean;
}): {fill: string; stroke: string} {
  // Connectors don't have fill - they're just `path`s. Return the 'main' color from
  // COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY as the stroke color.
  if ([AntibodyDomainType.H, AntibodyDomainType.J, AntibodyDomainType.L].includes(distinctDomain.type)) {
    const colorBank = hovered
      ? HOVER_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY
      : selected
        ? SELECTED_STROKE_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY
        : COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY;
    const stroke = distinctDomainToColor(distinctDomain, colorBank);
    return {fill: 'transparent', stroke};
  } else {
    const colorBank = hovered
      ? HOVER_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY
      : COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY;
    const fill = distinctDomainToColor(distinctDomain, colorBank);
    const stroke = selected
      ? distinctDomainToColor(distinctDomain, SELECTED_STROKE_COLORS_BY_DOMAIN_TYPE_AND_SPECIFICITY)
      : 'transparent';
    return {fill, stroke};
  }
}

/**
 * Maps each antibody domain ref to its corresponding domain.
 */
export function formatToDomainsByRef(format: AntibodyFormat): Record<number, AntibodyFormatDomain> {
  return format.domains.reduce<Record<number, AntibodyFormatDomain>>((acc, domain) => {
    acc[domain.ref] = domain;
    return acc;
  }, {});
}

/**
 * Builds a list of all distinct (domain type, specificity) pairs in the format. Sorts by chain, then by order
 * of each domain within the chain.
 * TODO(DIY Formats): Should we enforce that antibodies are 'symmetric', or that 'asymmetric' antibodies be multispecific?
 * TODO(DIY Formats): How should we determine chain order? Should we have a first class concept of heavy/light chains in addition to heavy/light domains?
 */
export function formatToSortedDistinctDomains(format: AntibodyFormat): Array<DistinctDomain> {
  const domainsByRef = formatToDomainsByRef(format);

  const distinctDomains: Array<DistinctDomain> = [];
  for (const chain of format.chains) {
    // O(nlogn), yuck, but we should (TM) only be dealing with O(100) domains at a time. Python lets you use
    // a set for tuples of primitives!!!
    for (const domainRef of chain.domainRefs) {
      const domain = domainsByRef[domainRef];
      const alreadyFound = distinctDomains.find(
        (other) => other.type === domain.type && other.specificity === domain.specificity
      );
      if (!alreadyFound) {
        distinctDomains.push({type: domain.type, specificity: domain.specificity});
      }
    }
  }
  return distinctDomains;
}
